import { navigate } from "gatsby";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useUrl from "../../hooks/useUrl";
import { SanityFormField } from "../../models/form/SanityFormField";
import {
  SanityInputCheckboxesProps,
  SanityInputRadioButtonsProps,
  SanityInputTextProps
} from "../../models/form/SanityFormInputField";
import {
  FormValues,
  SanityPriceCalculationFormProps
} from "../../models/form/SanityFormProps";
import { SanityInputFieldType } from "../../models/form/SanityInputFieldType";
import { LinkInfo } from "../../models/shared/LinkInfo";
import { isNullOrWhiteSpace } from "../../shared/string";
import { gtmDispatchEvent } from "../../utilities/gtm";
import { formatPhoneNumberLink } from "../../utilities/links";
import Button, { ButtonSize } from "../Buttons/Button";
import { Modal } from "../Modal";
import PortableText from "../PortableText";
import { PortableTextColorTheme } from "../PortableText/PortableText";
import SanityForm from "./SanityForm";
import { SelectionSummary, SelectionSummaryItem } from "./SelectionSummary";

interface PriceField {
  label: string;
  name: string;
  type: SanityInputFieldType;
  valuePriceMap: Record<string, number>;
}

const getPriceFields = (inputs: SanityFormField[]) => {
  const result: PriceField[] = [];

  inputs
    .filter(
      input =>
        (input._type === SanityInputFieldType.PriceCheckboxes ||
          input._type === SanityInputFieldType.PriceRadioButtons) &&
        !isNullOrWhiteSpace(input.slug?.current) &&
        input.options?.length
    )
    .forEach((input: SanityInputCheckboxesProps | SanityInputRadioButtonsProps) => {
      const valuePriceMap = input.options
        .filter(option => !isNullOrWhiteSpace(option.value))
        .reduce((optionMap, option) => {
          optionMap[option.value] = option.price ?? 0;
          return optionMap;
        }, {});

      result.push({
        name: input.slug.current,
        label: input.label ?? input.slug.current,
        type: input._type,
        valuePriceMap
      });

      for (const option of input.options) {
        if (option.inputs?.length) {
          const subFields = getPriceFields(option.inputs);
          result.push(...subFields);
        }
      }
    });

  return result;
};

const formatPrice = (
  value: number,
  currency?: string,
  showCurrencyAfterPrice = false
): string => {
  if (isNullOrWhiteSpace(currency)) {
    return value.toString();
  }

  return showCurrencyAfterPrice ? `${value} ${currency}` : `${currency} ${value}`;
};

const SanityPriceCalculatorForm = (props: SanityPriceCalculationFormProps) => {
  const totalFieldName = "__calculated-total";
  const [showResults, setShowResults] = useState(false);
  const [selectedValues, setSelectedValues] = useState<SelectionSummaryItem[]>([]);
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const resultRef = useRef<HTMLSelectElement>(null);

  const {
    _rawInputs,
    _rawPriceInformation,
    basePrice,
    contactButtonLabel,
    contactLink,
    currency,
    feedbackForm: feedbackFormArray,
    feedbackLabel,
    inputs,
    phoneNumber,
    priceDisclaimer,
    priceInformation,
    priceInformationTitle,
    priceTitle,
    showCurrencyAfterPrice,
    showSummary = true,
    summaryTitle,
    thankYouPage,
    useContactLink,
    useThankYouPage
  } = props;

  const thankYouPageUrl = useUrl({ reference: thankYouPage });

  const feedbackForm = feedbackFormArray?.[0];

  const { originalInputs, priceFields } = useMemo(() => {
    const originalInputs = (inputs || _rawInputs) ?? [];

    return {
      originalInputs,
      priceFields: getPriceFields(originalInputs)
    };
  }, [_rawInputs, inputs]);

  const calculateTotalPrice = (formValues: FormValues): number => {
    const summary: SelectionSummaryItem[] = [];

    const sum = priceFields.reduce((sum, priceField) => {
      const value = formValues[priceField.name];

      let price = 0;
      const summaryValues: string[] = [];

      if (Array.isArray(value)) {
        price = value.reduce((sum, val) => {
          if (!isNullOrWhiteSpace(val)) {
            summaryValues.push(val);
            const itemPrice = priceField.valuePriceMap[val];
            return itemPrice ? sum + itemPrice : sum;
          }

          return sum;
        }, 0);
      } else if (!isNullOrWhiteSpace(value)) {
        price = priceField.valuePriceMap[value] ?? 0;
        summaryValues.push(value);
      }

      if (summaryValues.length > 0) {
        summary.push({
          label: priceField.label,
          type: priceField.type,
          values: summaryValues
        });
      }

      sum = sum + price;

      return sum;
    }, basePrice ?? 0);

    setCalculatedTotal(sum);
    setSelectedValues(summary);

    return sum;
  };

  // callback for calculating the price and returning the updated value to the form
  const onModifyValue = (formValues: FormValues): FormValues => {
    const sum = calculateTotalPrice(formValues);

    return {
      [totalFieldName]: sum.toString()
    };
  };

  const handleSubmit = () => {
    if (useThankYouPage) {
      if (thankYouPage && !isNullOrWhiteSpace(thankYouPageUrl)) {
        navigate(thankYouPageUrl);
      }
    } else {
      setShowResults(true);
    }
  };

  // add calculated total field to form
  const inputsWithTotalField = useMemo(() => {
    const calculatedTotalInput: SanityInputTextProps = {
      _type: SanityInputFieldType.Text,
      hidden: true,
      readonly: true,
      slug: { current: totalFieldName }
    };

    return [...originalInputs, calculatedTotalInput];
  }, [originalInputs]);

  useEffect(() => {
    const resultSection = resultRef.current;
    if (showResults && resultSection) {
      resultSection.scrollIntoView({ behavior: "smooth" });
    }
  }, [showResults]);

  const price = formatPrice(calculatedTotal, currency, showCurrencyAfterPrice);

  const callUsLink: LinkInfo | undefined = useContactLink
    ? contactLink
    : {
        isExternalLink: true,
        title: contactButtonLabel,
        url: formatPhoneNumberLink(phoneNumber)
      };

  return (
    <>
      {showResults && (
        <section
          className="max-w-lg mx-auto scroll-mt-12 xl:scroll-mt-20 py-10 lg:py-14"
          ref={resultRef}
        >
          {!isNullOrWhiteSpace(priceTitle) && (
            <h2 className="text-center text-lg lg:text-2xl uppercase font-bold text-subheader-primary">
              {priceTitle}
            </h2>
          )}
          <p className="mt-0 text-center text-8xl lg:text-[7rem] leading-none font-bold text-brand-primary-20">
            {price}
          </p>
          {showSummary && !!selectedValues?.length && (
            <SelectionSummary
              additionalClassName="mt-10 lg:mt-14"
              items={selectedValues}
              title={summaryTitle ?? ""}
            />
          )}
          <div className="mt-10 lg:mt-14">
            {!isNullOrWhiteSpace(priceInformationTitle) && (
              <h3 className="text-sm lg:text-base uppercase font-bold text-subheader-primary">
                {priceInformationTitle}
              </h3>
            )}
            <PortableText
              _rawBody={priceInformation ?? _rawPriceInformation}
              addSections={false}
              colorTheme={PortableTextColorTheme.None}
            />
            {!isNullOrWhiteSpace(priceDisclaimer) && (
              <p className="mt-10 lg:mt-8 text-sm lg:text-base text-brand-primary-dim">
                {priceDisclaimer}
              </p>
            )}
          </div>
          <div className="mt-10 lg:mt-14 flex flex-col items-center">
            {!!callUsLink && !isNullOrWhiteSpace(callUsLink.title) && (
              <Button
                color="primary-light"
                size={ButtonSize.NormalWide}
                onClickLink={url =>
                  gtmDispatchEvent("priceFormContactCTA", {
                    priceFormContactCTAValue: url ?? ""
                  })
                }
                type="link"
                {...callUsLink}
              >
                {callUsLink.title}
              </Button>
            )}
            {!!feedbackForm && !isNullOrWhiteSpace(feedbackLabel) && (
              <button
                className="mt-8 text-lg text-brand-primary-dim underline hover:text-brand-primary-20"
                onClick={() => setShowFeedbackForm(true)}
              >
                {feedbackLabel}
              </button>
            )}
          </div>
        </section>
      )}

      {!showResults && (
        <SanityForm
          {...props}
          _rawInputs={undefined}
          gtmFormType="priceForm"
          gtmFormValueFieldName={totalFieldName}
          inputs={inputsWithTotalField}
          modifyValue={onModifyValue}
          onSubmitted={handleSubmit}
          variant="calculation-form"
        />
      )}

      {!!feedbackForm && !isNullOrWhiteSpace(feedbackLabel) && (
        <Modal isOpen={showFeedbackForm} onClose={() => setShowFeedbackForm(false)}>
          <SanityForm
            {...feedbackForm}
            onSubmitted={() => setShowFeedbackForm(false)}
            variant="modal"
          />
        </Modal>
      )}
    </>
  );
};

export default SanityPriceCalculatorForm;
